import {Component, OnDestroy, OnInit} from "@angular/core";
import {ActivatedRoute, Params, Router} from "@angular/router";
import {ServiceAccountService} from "./service-account.service";
import {RouteHelper, RouteMapping} from "../misc/route-helper";
import {AppSettingsService} from "../app-settings/app-settings.service";
import {ConfirmDialogService} from "../confirm-dialog/confirm-dialog-service";
import {DisplayTokenDialogComponent} from "./dialog-display-token.component";
import {UpdateTokenDialogComponent} from "./dialog-update-token.component";
import {DialogService} from "../bootstrap-modal/dialog.service";
import {handleErrors} from "../misc/handle-errors.decorator";
import {Subscription} from "rxjs";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 10;

@Component({
  selector: "manage-service-accounts",
  templateUrl: "./manage-service-account.template.pug",
  providers: [
    ServiceAccountService,
    ConfirmDialogService,
    DialogService,
  ],
})

export class ManageServiceAccountComponent extends RouteHelper implements OnInit, OnDestroy {
  serviceAccounts: any[] = [];
  currentPage: number = DEFAULT_PAGE;
  pageSize: number = DEFAULT_PAGE_LIMIT;
  maxPages: number;
  totalCount: number = 0;
  searchText: string = "";
  error: Error;
  isManager: boolean = false;
  private canSwitchAll: boolean = false;
  userSubscription: Subscription;

  paramsMap: RouteMapping[] = [
    {fieldName: "currentPage", paramName: "page", defaultValue: DEFAULT_PAGE, type: "number"} as RouteMapping,
    {fieldName: "pageSize", paramName: "limit", defaultValue: DEFAULT_PAGE_LIMIT, type: "number"} as RouteMapping,
    {fieldName: "searchText", paramName: "search"} as RouteMapping,
  ];

  constructor(
    private serviceAccountService: ServiceAccountService,
    public route: ActivatedRoute,
    public router: Router,
    public appSettingsService: AppSettingsService,
    public confirmDialogService: ConfirmDialogService,
    public dialogService: DialogService,
  ) {
    super();
  }

  ngOnInit() {
    super.ngOnInit();

    this.userSubscription = this.appSettingsService.user.subscribe((user) => {
      this.isManager = user.privilegeFlags["manager"];
      this.canSwitchAll = user.privilegeFlags["switch-user"];
    });
  }

  ngOnDestroy() {
    if (this.userSubscription && !this.userSubscription.closed) {
      this.userSubscription.unsubscribe();
    }
  }

  navigate(queryParams: Params, skipLocationChange: boolean = false): Promise<boolean> {
    return this.router.navigate(["service-accounts"], {queryParams, skipLocationChange});
  }

  @handleErrors()
  async loadData(): Promise<void> {
    const {rows, count} = await this.serviceAccountService.getServiceAccounts(
      (+this.currentPage) - 1,
      (+this.pageSize),
      this.searchText,
    );

    this.serviceAccounts = rows;
    this.maxPages = Math.ceil(count / (+this.pageSize));
    this.totalCount = count;
  }

  onListControlsChange(event) {
    switch (event["key"]) {
      case "page":
        this.currentPage = event["value"];
        break;
      case "search":
        this.currentPage = DEFAULT_PAGE;
        this.searchText = event["value"];
        break;
      case "limit":
        this.currentPage = DEFAULT_PAGE;
        this.pageSize = event["value"];
        break;
      default:
        console.info(`Unknown ${event["key"]}`);
        break;
    }

    this.syncRoute();
  }

  @handleErrors()
  async resetIncomingToken(id, uid) {
    const result = await this.confirmDialogService.confirm(
      "Confirm",
      `You have requested a new Incoming token for the ${uid} service account.
      The old token will cease to function.
      Do you want to Continue?`,
    );

    if (!result) return;

    const {data, authenticationMechanism} = await this.serviceAccountService.resetIncomingToken(id);

    await DisplayTokenDialogComponent.showDisplayTokenDialogModal(this.dialogService, {data, authenticationMechanism});
  }

  @handleErrors()
  async resetOutgoingToken(id, uid) {
    const result = await this.confirmDialogService.confirm(
      "Confirm",
      `You have requested a new Outgoing token for the ${uid} service account.
      The old token will cease to function.
      Do you want to Continue?`,
    );

    if (!result) return;

    const {data, authenticationMechanism} = await this.serviceAccountService.resetOutgoingToken(id);

    await DisplayTokenDialogComponent.showDisplayTokenDialogModal(this.dialogService, {data, authenticationMechanism});
  }

  @handleErrors()
  async updateOutgoingToken(id) {
    const result = await UpdateTokenDialogComponent.showUpdateTokenDialogModal(
      this.dialogService,
      {
        accountId: id,
        message: "Outgoing",
      },
    ).toPromise();

    if (!result.success) return;
    const data = result.data;
    const authenticationMechanism = result.authenticationMechanism;
    await DisplayTokenDialogComponent.showDisplayTokenDialogModal(this.dialogService, {data, authenticationMechanism});
  }

  @handleErrors()
  async useIncomingAsOutgoingToken(id) {
    const result = await this.confirmDialogService.confirm(
      "Confirm",
      "Are you sure you want to use the incoming token as outgoing token?",
    );

    if (!result) return;
    await this.serviceAccountService.useIncomingAsOutgoingToken(id);
  }
}
